import React, { useSyncExternalStore } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTour } from '@reactour/tour';
import { setTourOutcome } from './tourOutcome';
import { subscribeTourGate, getTourGateSnapshot } from './tourGate';

type TailPosition = 'top' | 'right' | 'bottom' | 'left' | 'center';

interface TourStepContentProps {
	title?: string;
	body: React.ReactNode;
	/**
	 * Gate step: hide Next/Finish. The user must perform the highlighted action
	 * (click a menu item, open a drawer, …) and the tour auto-advances via its
	 * sync controller. Skip/Back remain available.
	 */
	awaitAction?: boolean;
	/**
	 * Where the popover sits relative to the target (same value as the step's
	 * `position`). Draws a speech-bubble tail pointing at the target. Omit or
	 * 'center' for general slides with no tail.
	 */
	tailPosition?: TailPosition;
	/**
	 * Shifts the tail along the popover edge instead of centering it. Positive =
	 * px from the popover's right edge (top/bottom tails) or bottom edge
	 * (left/right tails), for a box aligned to the target's right/bottom. Negative
	 * = px from the opposite edge (left/top), for a box aligned to the target's
	 * left/top. Use when the box is edge-aligned so the tail still points at the
	 * target. Omit to keep the tail centered.
	 */
	tailOffset?: number;
	/**
	 * Name of a gate (see tourGate.ts) that must be satisfied before Next/Finish
	 * enables — e.g. require a meeting name before advancing. The host component
	 * publishes the gate's state with setTourGate. Unlike awaitAction the button
	 * stays visible, just disabled. Omit for no validation.
	 */
	requires?: string;
	/**
	 * Custom Back handler. By default Back goes to the previous step index, which
	 * is wrong for out-of-order / action-driven steps (e.g. the ticketing tour's
	 * manual-entry step, appended at the end). Provide this to run your own logic
	 * instead — typically undo the app action so the sync hook drives the tour
	 * back to the right step.
	 */
	onBack?: () => void;
	/**
	 * Custom Next handler. By default Next advances to the next step index; provide
	 * this to run your own logic instead — e.g. trigger the real form submit so the
	 * action-driven sync hook advances the tour once the app moves stage. Only used
	 * when the step isn't awaitAction (i.e. Next is visible).
	 */
	onNext?: () => void;
	/**
	 * Hide the Back button on this step (e.g. steps you shouldn't be able to
	 * reverse into, or where "back" has no meaningful target). Skip/Next stay.
	 */
	hideBack?: boolean;
	/**
	 * Force the completion ("Finish") button regardless of array position. Needed
	 * because some tours append out-of-flow steps after their finish slide, so the
	 * finish step is no longer the last array element.
	 */
	finalStep?: boolean;
}

// Popover padding (must match tourStyles.popover.padding in App.jsx) + tail size.
const POPOVER_PADDING = 20;
const TAIL = 9;
const TAIL_COLOR = '#eeeeee';

// CSS triangle pointing toward the target, flush against the popover edge.
const tailSx = (pos: TailPosition, offset?: number) => {
	const transparent = `${TAIL}px solid transparent`;
	const solid = `${TAIL}px solid ${TAIL_COLOR}`;
	// Outline only the two OUTWARD edges + tip so it reads as a pointer, not a
	// boxed triangle. Zero-blur drop-shadows trace the CSS-border triangle; a
	// shadow on all four sides would also outline the BASE (where the tail meets
	// the popover), and that line runs parallel to the box's own border — looking
	// doubled. So we drop the one shadow that points from the tail back toward the
	// popover body (its base edge) and keep the other three.
	const SHADOW: Record<string, string> = {
		up: 'drop-shadow(0 -2px 0 #000)',
		down: 'drop-shadow(0 2px 0 #000)',
		left: 'drop-shadow(-2px 0 0 #000)',
		right: 'drop-shadow(2px 0 0 #000)',
	};
	// Direction from the tail back toward the popover body = the base edge to skip.
	const towardBox: Record<string, string> = {
		top: 'up',
		bottom: 'down',
		left: 'left',
		right: 'right',
	};
	const outline = Object.keys(SHADOW)
		.filter((dir) => dir !== towardBox[pos])
		.map((dir) => SHADOW[dir])
		.join(' ');
	const base = {
		position: 'absolute' as const,
		width: 0,
		height: 0,
		filter: outline,
	};
	// Along the horizontal edge (top/bottom tails) or vertical edge (left/right).
	// Positive offset = px from the popover's right/bottom edge (for boxes aligned
	// to the target's right/bottom); negative = px from the left/top edge (for
	// boxes aligned to the target's left/top); omitted = centered.
	const horiz =
		offset == null
			? { left: '50%', transform: 'translateX(-50%)' }
			: offset < 0
				? { left: -offset }
				: { right: offset };
	const vert =
		offset == null
			? { top: '50%', transform: 'translateY(-50%)' }
			: offset < 0
				? { top: -offset }
				: { bottom: offset };
	switch (pos) {
		case 'right': // popover right of target -> tail on left edge, points left
			return {
				...base,
				...vert,
				left: -(POPOVER_PADDING + TAIL),
				borderTop: transparent,
				borderBottom: transparent,
				borderRight: solid,
			};
		case 'left': // popover left of target -> tail on right edge, points right
			return {
				...base,
				...vert,
				right: -(POPOVER_PADDING + TAIL),
				borderTop: transparent,
				borderBottom: transparent,
				borderLeft: solid,
			};
		case 'bottom': // popover below target -> tail on top edge, points up
			return {
				...base,
				...horiz,
				top: -(POPOVER_PADDING + TAIL),
				borderLeft: transparent,
				borderRight: transparent,
				borderBottom: solid,
			};
		case 'top': // popover above target -> tail on bottom edge, points down
			return {
				...base,
				...horiz,
				bottom: -(POPOVER_PADDING + TAIL),
				borderLeft: transparent,
				borderRight: transparent,
				borderTop: solid,
			};
		default:
			return null;
	}
};

// Shared popover body + footer for tour steps. Provides explicit Skip / Back /
// Next (Finish on the last step) controls — reactour's default close/nav chrome
// is disabled in favour of these so the outcome (skipped vs completed) is clear.
const TourStepContent: React.FC<TourStepContentProps> = ({
	title,
	body,
	awaitAction,
	tailPosition,
	tailOffset,
	requires,
	onBack,
	onNext,
	hideBack,
	finalStep,
}) => {
	const { currentStep, steps, setCurrentStep, setIsOpen } = useTour();

	// Re-renders when the required gate flips; true when no gate is required.
	const gateSatisfied = useSyncExternalStore(subscribeTourGate, () =>
		requires ? getTourGateSnapshot(requires) : true,
	);

	const total = steps?.length ?? 0;
	const isFirst = currentStep <= 0;
	const isLast = currentStep >= total - 1;
	// A final step is one actually showing the Finish button — its only action is
	// to finish, so we drop Close and Back there. (Not just isLast: some tours
	// append out-of-flow steps after their finish slide.)
	const showingFinish = !awaitAction && (finalStep || isLast);

	const skip = () => {
		setTourOutcome('skipped');
		setIsOpen(false);
	};

	const finish = () => {
		setTourOutcome('completed');
		setIsOpen(false);
	};

	const tail =
		tailPosition && tailPosition !== 'center'
			? tailSx(tailPosition, tailOffset)
			: null;

	return (
		<Box data-tour-popover sx={{ minWidth: 240, position: 'relative' }}>
			{tail && <Box sx={tail} />}
			{title && (
				<Typography variant="h6" fontWeight={600} gutterBottom>
					{title}
				</Typography>
			)}
			<Typography variant="body2" color="text.secondary" component="div">
				{body}
			</Typography>

			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: showingFinish ? 'flex-end' : 'space-between',
					mt: 2,
				}}
			>
				{!showingFinish && (
					<Button
						size="small"
						color="inherit"
						onClick={skip}
						sx={{ textTransform: 'none', color: 'text.secondary' }}
					>
						Close
					</Button>
				)}
				<Box sx={{ display: 'flex', gap: 1 }}>
					{!isFirst && !hideBack && !showingFinish && (
						<Button
							size="small"
							onClick={
								onBack ?? (() => setCurrentStep(currentStep - 1))
							}
							sx={{ textTransform: 'none' }}
						>
							Back
						</Button>
					)}
					{!awaitAction &&
						(finalStep || isLast ? (
							<Button
								size="small"
								variant="contained"
								disabled={!gateSatisfied}
								onClick={finish}
								sx={{ textTransform: 'none' }}
							>
								Finish
							</Button>
						) : (
							<Button
								size="small"
								variant="contained"
								disabled={!gateSatisfied}
								onClick={
									onNext ?? (() => setCurrentStep(currentStep + 1))
								}
								sx={{ textTransform: 'none' }}
							>
								Next
							</Button>
						))}
				</Box>
			</Box>
		</Box>
	);
};

export default TourStepContent;
